import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, switchMap } from 'rxjs';

import { FileUtils } from '@utils/file.utils';

import { RESTServerRoute } from '../../types/Server';
import { DailyReport, DailyReportResult } from '../../types/Chatbot';
import { API_VERSION_CTX, ApiVersion } from '../../types/api';
import { SKIP_AUTO_DISCONNECT } from '../../interceptors/auto-disconnect/auto-disconnect.interceptor';

@Injectable({
  providedIn: 'root',
})
export class ChatDailyReportApiService {
  public constructor(private readonly httpClient: HttpClient) {}

  // The response shape is role-dependent (decided server-side): JSON metadata
  // for tenant admins, a ZIP of per-site PDFs for site admins. Fetch as a blob
  // and branch on the Content-Type instead of duplicating the role logic here.
  public getLatestReport(): Observable<DailyReportResult> {
    return this.httpClient
      .get(RESTServerRoute.REST_DAILY_REPORT_LATEST, {
        responseType: 'blob',
        observe: 'response',
        context: new HttpContext().set(API_VERSION_CTX, ApiVersion.V2).set(SKIP_AUTO_DISCONNECT, true),
      })
      .pipe(switchMap((response) => from(this.toResult(response))));
  }

  private async toResult(response: HttpResponse<Blob>): Promise<DailyReportResult> {
    const blob = response.body ?? new Blob();
    const contentType = response.headers.get('Content-Type') ?? '';

    if (contentType.includes('application/zip')) {
      return { kind: 'zip', blob, filename: this.zipFilename(response) };
    }

    const report = JSON.parse(await this.readBlobText(blob)) as DailyReport;
    return { kind: 'merged', report };
  }

  // Blob.text() is missing in some environments (jsdom, older Safari).
  private readBlobText(blob: Blob): Promise<string> {
    if (typeof blob.text === 'function') {
      return blob.text();
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });
  }

  private zipFilename(response: HttpResponse<Blob>): string {
    const filename = FileUtils.extractFilenameFromResponse(response);
    if (filename) {
      return filename;
    }
    const today = new Date().toISOString().slice(0, 10);
    return `daily-reports-${today}.zip`;
  }
}
